import React from 'react';
import PropTypes from 'prop-types';
import {
  Grid,
  TextField,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Typography,
  Paper,
  Switch,
  Chip,
  Divider,
  Box,
  Toolbar
} from '@material-ui/core';
import IconArrowBack from '@material-ui/icons/ArrowBack';
import IconCheck from '@material-ui/icons/Check';
import IconBusinessCenter from '@material-ui/icons/BusinessCenter';
import IconCloudUpload from '@material-ui/icons/CloudUpload';
import IconVisibility from '@material-ui/icons/Visibility';
import IconSearch from '@material-ui/icons/Search';
import IconAttachMoney from '@material-ui/icons/AttachMoney';
import { getImageSource } from "../api/routes";
import ValidationTooltip from "../../common/ValidationTooltip";

const nullToEmptyString = (value) => (value === null || value === undefined ? "" : value);

class BusinessForm extends React.Component {
  render() {
    const {
      product,
      errors = {},
      activeErrorField,
      previewImage,
      vendors,
      categories,
      permissions,
      isAdmin,
      productId,
      onType,
      onSelect,
      onChangePublished,
      onChangeFeatured,
      onUpload,
      onSubmit,
      back,
      isLoadingSubmit,
      isUploadingFile
    } = this.props;

    // Find category name for preview
    let categoryName = "Business Solution";
    if (categories?.list) {
      categories.list.forEach(catGroup => {
        if (catGroup?.category?._id === product.categoryId) {
          categoryName = catGroup.category.name;
        }
        if (catGroup?.subCategories) {
          catGroup.subCategories.forEach(sub => {
            if (sub?._id === product.categoryId) {
              categoryName = sub.name;
            }
          });
        }
      });
    }

    let submitButtonText = "Create Solution";
    if (isLoadingSubmit) {
      submitButtonText = "Saving...";
    } else if (productId) {
      submitButtonText = "Save Changes";
    }

    let uploadButtonText = "Upload Image";
    if (isUploadingFile) {
      uploadButtonText = "Uploading...";
    } else if (product.image && product.image !== "default.jpg") {
      uploadButtonText = "Change Image";
    }

    return (
      <form onSubmit={onSubmit} noValidate>
        {/* Top Header Toolbar */}
        <Toolbar style={{ borderBottom: "1px solid rgba(0, 0, 0, 0.12)" }}>
          <IconButton type="button" onClick={back} style={{ marginRight: 8, color: '#0d3559' }}>
            <IconArrowBack />
          </IconButton>
          <Box flexGrow={1}>
            <Typography variant="h6" style={{ fontWeight: 700, color: '#0d3559' }}>
              {productId ? "Edit Business Solution" : "Add Business Solution"}
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" style={{ gap: 10 }}>
            <Button
              type="button"
              variant="outlined"
              onClick={back}
              disabled={isLoadingSubmit}
              style={{
                borderRadius: 4,
                textTransform: 'none',
                fontWeight: 600,
                borderColor: '#d0d7de',
                color: '#24292f'
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              type="submit"
              disabled={isLoadingSubmit || !permissions.canEdit}
              startIcon={<IconCheck />}
              style={{
                borderRadius: 4,
                textTransform: 'none',
                fontWeight: 600,
                backgroundColor: '#0d3559',
                color: '#ffffff',
                boxShadow: 'none'
              }}
            >
              {submitButtonText}
            </Button>
          </Box>
        </Toolbar>

        <div style={{ padding: '20px 24px 32px 24px' }}>
          <Grid container spacing={3}>
            {/* Left Main Form Column */}
            <Grid item xs={12} lg={8}>

              {/* Card 1: Core Solution Details */}
              <Paper elevation={0} style={{ padding: 24, borderRadius: 8, border: '1px solid rgba(0, 0, 0, 0.12)', marginBottom: 24, backgroundColor: '#ffffff' }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <IconBusinessCenter style={{ color: '#0d3559', marginRight: 10 }} />
                  <Typography variant="h6" style={{ fontWeight: 700, color: '#0d3559', fontSize: 18 }}>
                    Product Details
                  </Typography>
                </Box>
                <Divider style={{ marginBottom: 20 }} />

                <Grid container spacing={2}>
                  {/* Admin Vendor Selection */}
                  {isAdmin && (
                    <Grid item xs={12} style={{ position: 'relative' }}>
                      <FormControl fullWidth margin="dense" variant="standard" error={Boolean(errors.vendorId)} style={{ marginBottom: 8 }}>
                        <InputLabel id="vendor-select-label" required>Select Vendor / Account</InputLabel>
                        <Select
                          labelId="vendor-select-label"
                          value={nullToEmptyString(product.vendorId)}
                          onChange={onSelect}
                          label="Select Vendor / Account"
                          inputProps={{ name: "vendorId", id: "vendorId" }}
                        >
                          <MenuItem value=""><em>Select Vendor Account</em></MenuItem>
                          {vendors?.map(v => (
                            <MenuItem key={v._id} value={v._id}>
                              {v.name} ({v.email})
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.vendorId && <FormHelperText error>{errors.vendorId}</FormHelperText>}
                      </FormControl>
                      {activeErrorField === 'vendorId' && (
                        <ValidationTooltip message={errors.vendorId || "Please fill out this field."} />
                      )}
                    </Grid>
                  )}

                  {/* Product Title */}
                  <Grid item xs={12} style={{ position: 'relative' }}>
                    <TextField
                      name="name"
                      value={nullToEmptyString(product.name)}
                      onChange={onType}
                      label="Product / Solution Title"
                      placeholder="e.g. Daisy Business Support Bot"
                      required
                      error={Boolean(errors.name)}
                      helperText={errors.name || ""}
                      variant="standard"
                      margin="dense"
                      fullWidth
                    />
                    {activeErrorField === 'name' && (
                      <ValidationTooltip message={errors.name || "Please fill out this field."} />
                    )}
                  </Grid>

                  {/* Slug & Brand */}
                  <Grid item xs={12} sm={6} style={{ position: 'relative' }}>
                    <TextField
                      name="slug"
                      id="slug"
                      value={nullToEmptyString(product.slug)}
                      onChange={onType}
                      label="URL Slug"
                      placeholder="e.g. daisy-support-bot"
                      required
                      error={Boolean(errors.slug)}
                      helperText={errors.slug || ""}
                      variant="standard"
                      margin="dense"
                      fullWidth
                    />
                    {activeErrorField === 'slug' && (
                      <ValidationTooltip message={errors.slug || "Please fill out this field."} />
                    )}
                  </Grid>

                  <Grid item xs={12} sm={6} style={{ position: 'relative' }}>
                    <TextField
                      name="brand"
                      id="brand"
                      value={nullToEmptyString(product.brand)}
                      onChange={onType}
                      label="Brand / Suite Name"
                      placeholder="e.g. Daisy AI"
                      required
                      error={Boolean(errors.brand)}
                      helperText={errors.brand || ""}
                      variant="standard"
                      margin="dense"
                      fullWidth
                    />
                    {activeErrorField === 'brand' && (
                      <ValidationTooltip message={errors.brand || "Please fill out this field."} />
                    )}
                  </Grid>

                  {/* Category Selection */}
                  <Grid item xs={12} style={{ position: 'relative' }}>
                    <FormControl fullWidth margin="dense" variant="standard" error={Boolean(errors.categoryId)}>
                      <InputLabel id="category-select-label" required>Category</InputLabel>
                      <Select
                        labelId="category-select-label"
                        value={nullToEmptyString(product.categoryId)}
                        onChange={onSelect}
                        label="Category"
                        inputProps={{ name: "categoryId", id: "categoryId" }}
                      >
                        <MenuItem value=""><em>None</em></MenuItem>
                        {categories?.list?.map(({ category, subCategories }) => [
                          <MenuItem key={category._id} value={category._id} disabled style={{ fontWeight: 700, color: '#0d3559' }}>
                            {category.name}
                          </MenuItem>,
                          subCategories?.map(sub => (
                            <MenuItem key={sub._id} value={sub._id} style={{ paddingLeft: 32 }}>
                              {sub.name}
                            </MenuItem>
                          ))
                        ])}
                      </Select>
                      {errors.categoryId && <FormHelperText error>{errors.categoryId}</FormHelperText>}
                    </FormControl>
                    {activeErrorField === 'categoryId' && (
                      <ValidationTooltip message={errors.categoryId || "Please fill out this field."} />
                    )}
                  </Grid>

                  {/* Short Description */}
                  <Grid item xs={12} style={{ position: 'relative' }}>
                    <TextField
                      name="description"
                      value={nullToEmptyString(product.description)}
                      onChange={onType}
                      label="Short Description"
                      placeholder="Enter a brief summary overview of your business product or service..."
                      required
                      error={Boolean(errors.description)}
                      helperText={errors.description || ""}
                      multiline
                      minRows={2}
                      maxRows={6}
                      variant="standard"
                      margin="dense"
                      fullWidth
                    />
                    {activeErrorField === 'description' && (
                      <ValidationTooltip message={errors.description || "Please fill out this field."} />
                    )}
                  </Grid>

                  {/* Detailed Description */}
                  <Grid item xs={12}>
                    <TextField
                      name="detailedDescription"
                      value={nullToEmptyString(product.detailedDescription)}
                      onChange={onType}
                      label="Detailed Description"
                      placeholder="Enter comprehensive details, specifications, integration guides, or terms for this product..."
                      multiline
                      minRows={3}
                      maxRows={10}
                      variant="standard"
                      margin="dense"
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Card 2: Pricing & Unit Specs */}
              <Paper elevation={0} style={{ padding: 24, borderRadius: 8, border: '1px solid rgba(0, 0, 0, 0.12)', marginBottom: 24, backgroundColor: '#ffffff' }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <IconAttachMoney style={{ color: '#0d3559', marginRight: 10 }} />
                  <Typography variant="h6" style={{ fontWeight: 700, color: '#0d3559', fontSize: 18 }}>
                    Pricing & Unit Configuration
                  </Typography>
                </Box>
                <Divider style={{ marginBottom: 20 }} />

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      name="storePrice"
                      type="number"
                      value={nullToEmptyString(product.storePrice)}
                      onChange={onType}
                      label="Price (£)"
                      placeholder="0.00"
                      variant="standard"
                      margin="dense"
                      fullWidth
                      helperText="Leave 0 for custom enquiry / free tier"
                    />
                  </Grid>

                  <Grid item xs={12} sm={4} style={{ position: 'relative' }}>
                    <TextField
                      name="priceDiscount"
                      id="priceDiscount"
                      type="number"
                      value={nullToEmptyString(product.priceDiscount)}
                      onChange={onType}
                      onKeyDown={(e) => {
                        if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                          e.preventDefault();
                        }
                      }}
                      label="Discount (%)"
                      placeholder="0"
                      variant="standard"
                      margin="dense"
                      fullWidth
                      inputProps={{ min: 0, max: 100 }}
                      error={Boolean(errors.priceDiscount)}
                      helperText={errors.priceDiscount || ""}
                    />
                    {activeErrorField === "priceDiscount" && (
                      <ValidationTooltip message={errors.priceDiscount || "Please enter a value between 0 and 100."} />
                    )}
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      name="priceDiscounted"
                      type="number"
                      value={nullToEmptyString(product.priceDiscounted)}
                      label="Discounted Price (£)"
                      placeholder="0.00"
                      disabled
                      variant="standard"
                      margin="dense"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      name="unit"
                      value={nullToEmptyString(product.unit)}
                      onChange={onType}
                      label="Unit (Billing Cycle)"
                      placeholder="e.g. Month, Year, User, Project"
                      variant="standard"
                      margin="dense"
                      fullWidth
                      helperText="e.g. Month, Year, User, License"
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      name="buttonText"
                      value={nullToEmptyString(product.buttonText)}
                      onChange={onType}
                      label="Action Button Text"
                      placeholder="e.g. Get Started, Subscribe, Book a Demo"
                      variant="standard"
                      margin="dense"
                      fullWidth
                      helperText="Defaults to Subscribe"
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      name="buttonLink"
                      value={nullToEmptyString(product.buttonLink)}
                      onChange={onType}
                      label="Action Button Link (URL)"
                      placeholder="e.g. https://... or /onboarding"
                      variant="standard"
                      margin="dense"
                      fullWidth
                      helperText="Destination link opened in new tab"
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Card 3: SEO Keywords */}
              <Paper elevation={0} style={{ padding: 24, borderRadius: 8, border: '1px solid rgba(0, 0, 0, 0.12)', marginBottom: 24, backgroundColor: '#ffffff' }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <IconSearch style={{ color: '#0d3559', marginRight: 10 }} />
                  <Typography variant="h6" style={{ fontWeight: 700, color: '#0d3559', fontSize: 18 }}>
                    Search Engine Optimization (SEO)
                  </Typography>
                </Box>
                <Divider style={{ marginBottom: 20 }} />

                <TextField
                  name="seo.keywords"
                  value={nullToEmptyString(product.seo?.keywords)}
                  onChange={onType}
                  label="SEO Target Keywords"
                  placeholder="e.g. AI Support Bot, RAG Knowledge, Business Automation, Customer Care"
                  variant="standard"
                  margin="dense"
                  fullWidth
                  helperText="Separate keywords with commas for search indexing."
                />
              </Paper>
            </Grid>

            {/* Right Column: Settings, Image Upload & Live Card Preview */}
            <Grid item xs={12} lg={4}>

              {/* Card 4: Solution Status Toggles */}
              <Paper elevation={0} style={{ padding: 24, borderRadius: 8, border: '1px solid rgba(0, 0, 0, 0.12)', marginBottom: 24, backgroundColor: '#ffffff' }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <IconVisibility style={{ color: '#0d3559', marginRight: 10 }} />
                  <Typography variant="h6" style={{ fontWeight: 700, color: '#0d3559', fontSize: 18 }}>
                    Publishing & Status
                  </Typography>
                </Box>
                <Divider style={{ marginBottom: 16 }} />

                <Box display="flex" alignItems="center" justifyContent="space-between" py={1}>
                  <Box>
                    <Typography variant="subtitle2" style={{ fontWeight: 600, color: '#0d3559' }}>
                      Publish Solution
                    </Typography>
                    <Typography variant="caption" style={{ color: '#666666' }}>
                      Make this product visible in catalog
                    </Typography>
                  </Box>
                  <Switch
                    checked={Boolean(product.isPublished)}
                    onChange={onChangePublished}
                    color="primary"
                  />
                </Box>
                <Divider style={{ margin: '8px 0' }} />

                <Box display="flex" alignItems="center" justifyContent="space-between" py={1}>
                  <Box>
                    <Typography variant="subtitle2" style={{ fontWeight: 600, color: '#0d3559' }}>
                      Featured Solution
                    </Typography>
                    <Typography variant="caption" style={{ color: '#666666' }}>
                      Highlight on main dashboard & landing pages
                    </Typography>
                  </Box>
                  <Switch
                    checked={Boolean(product.isFeatured)}
                    onChange={onChangeFeatured}
                    color="primary"
                  />
                </Box>
              </Paper>

              {/* Card 5: Media Assets */}
              <Paper elevation={0} style={{ padding: 24, borderRadius: 8, border: '1px solid rgba(0, 0, 0, 0.12)', marginBottom: 24, backgroundColor: '#ffffff' }}>
                <Typography variant="h6" style={{ fontWeight: 700, color: '#0d3559', fontSize: 18, marginBottom: 4 }}>
                  Product Image Asset
                </Typography>
                <Typography variant="caption" style={{ color: '#666666', display: 'block', marginBottom: 16 }}>
                  Recommended Dimensions: 300px × 300px (Max 250KB)
                </Typography>

                <Box textAlign="center">
                  <Box
                    style={{
                      border: '1px solid rgba(0, 0, 0, 0.12)',
                      borderRadius: 8,
                      padding: 12,
                      backgroundColor: '#f8fafc',
                      marginBottom: 16
                    }}
                  >
                    <img
                      src={previewImage || getImageSource(product.image)}
                      alt="Primary Product"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = getImageSource('default.jpg');
                      }}
                      style={{
                        width: '100%',
                        maxHeight: 220,
                        objectFit: 'contain',
                        borderRadius: 6
                      }}
                    />
                  </Box>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="raised-button-file-change"
                    type="file"
                    onChange={onUpload}
                  />
                  <label htmlFor="raised-button-file-change">
                    <Button
                      variant="outlined"
                      component="span"
                      disabled={isUploadingFile}
                      startIcon={<IconCloudUpload />}
                      style={{
                        borderRadius: 4,
                        textTransform: 'none',
                        fontWeight: 600,
                        borderColor: '#0d3559',
                        color: '#0d3559'
                      }}
                    >
                      {uploadButtonText}
                    </Button>
                  </label>
                </Box>
              </Paper>

              {/* Card 6: Live Solution Card Preview */}
              <Paper elevation={0} style={{ padding: 20, borderRadius: 8, backgroundColor: '#0d3559', color: '#ffffff' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Chip label="LIVE CATALOG PREVIEW" size="small" style={{ backgroundColor: 'rgba(255, 255, 255, 0.15)', color: '#ffffff', fontWeight: 700, fontSize: 10 }} />
                  <IconBusinessCenter style={{ color: '#ffffff', fontSize: 18 }} />
                </Box>

                <Box style={{ backgroundColor: 'rgba(255, 255, 255, 0.08)', borderRadius: 6, padding: 16 }}>
                  {(previewImage || product.image) && (
                    <Box mb={1.5} textAlign="center">
                      <img
                        src={previewImage || getImageSource(product.image)}
                        alt="Preview"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = getImageSource();
                        }}
                        style={{ width: '100%', maxHeight: 90, objectFit: 'contain', borderRadius: 4 }}
                      />
                    </Box>
                  )}
                  <Typography variant="caption" style={{ color: 'rgba(255, 255, 255, 0.7)', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>
                    {categoryName}
                  </Typography>
                  <Typography variant="h6" style={{ fontWeight: 700, color: '#ffffff', marginTop: 4 }}>
                    {product.name || "Solution Title"}
                  </Typography>
                  {(() => {
                    const rawPrice = Number.parseFloat(product.price || product.storePrice || 0) || 0;
                    const discountPct = Number.parseFloat(product.priceDiscount || 0) || 0;
                    const validDiscount = discountPct >= 0 && discountPct <= 100 ? discountPct : 0;
                    const finalPrice = validDiscount > 0 ? (rawPrice - (rawPrice * validDiscount / 100)) : rawPrice;
                    if (rawPrice <= 0) return null;
                    return (
                      <Box display="flex" alignItems="baseline" mt={0.5} mb={0.5} style={{ gap: 6 }}>
                        {discountPct > 0 && (
                          <Typography variant="body2" style={{ textDecoration: 'line-through', color: 'rgba(255,255,255,0.6)', fontWeight: 500, fontSize: 13 }}>
                            £{rawPrice.toFixed(2)}
                          </Typography>
                        )}
                        <Typography variant="subtitle1" style={{ fontWeight: 800, color: '#ffffff', fontSize: 18 }}>
                          £{finalPrice.toFixed(2)}
                        </Typography>
                        {product.unit && (
                          <Typography variant="caption" style={{ color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>
                            / {product.unit}
                          </Typography>
                        )}
                      </Box>
                    );
                  })()}
                  <Typography variant="body2" style={{ color: 'rgba(255, 255, 255, 0.85)', marginTop: 6, fontSize: 13, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {product.description || "Enter short description to see preview..."}
                  </Typography>
                  {product.buttonText && (
                    <Box mt={1.5}>
                      <Button size="small" variant="contained" style={{ backgroundColor: '#ffffff', color: '#0d3559', fontWeight: 700, fontSize: 12, textTransform: 'none', borderRadius: 4 }}>
                        {product.buttonText}
                      </Button>
                    </Box>
                  )}
                </Box>
              </Paper>

            </Grid>
          </Grid>

          {/* Bottom Action Bar */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
            mt={4}
            pt={2}
            pb={2}
            borderTop="1px solid rgba(0, 0, 0, 0.12)"
            style={{ gap: 12 }}
          >
            <Button
              type="button"
              variant="outlined"
              onClick={back}
              disabled={isLoadingSubmit}
              style={{
                borderRadius: 4,
                textTransform: 'none',
                fontWeight: 600,
                borderColor: '#d0d7de',
                color: '#24292f',
                padding: '6px 18px'
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              type="submit"
              disabled={isLoadingSubmit || !permissions.canEdit}
              startIcon={<IconCheck />}
              style={{
                borderRadius: 4,
                textTransform: 'none',
                fontWeight: 600,
                backgroundColor: '#0d3559',
                color: '#ffffff',
                boxShadow: 'none',
                padding: '6px 20px'
              }}
            >
              {submitButtonText}
            </Button>
          </Box>
        </div>
      </form>
    );
  }
}

BusinessForm.propTypes = {
  product: PropTypes.object.isRequired,
  activeErrorField: PropTypes.string,
  previewImage: PropTypes.string,
  vendors: PropTypes.array,
  categories: PropTypes.object,
  permissions: PropTypes.object,
  isAdmin: PropTypes.bool,
  productId: PropTypes.string,
  onType: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  onChangePublished: PropTypes.func.isRequired,
  onChangeFeatured: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  back: PropTypes.func.isRequired,
  isLoadingSubmit: PropTypes.bool,
  isUploadingFile: PropTypes.bool
};

export default BusinessForm;
